/**
 * Панель выбора шаблонов уровня.
 *
 * Слева каталог шаблонов партнёра с поиском и ценами, справа — выбранное:
 * по шаблону на каждый ролик уровня, сверху вниз в порядке показа.
 *
 * СКОЛЬКО ШАБЛОНОВ — СТОЛЬКО У УРОВНЯ РОЛИКОВ, от одного до
 * `MAX_CLIPS_PER_LEVEL`. Раньше панель требовала ровно столько, сколько
 * роликов описано в файле сценария, а там у каждого уровня главы 1 ровно
 * один (у шести последних — ни одного), поэтому завести уровню второй
 * ролик было нечем вовсе.
 *
 * ДВЕ ГРАНИЦЫ, И ОБЕ НЕ ПРОИЗВОЛЬНЫЕ:
 *
 *  • сверху — `MAX_CLIPS_PER_LEVEL`: длину прохода обещает индикатор
 *    «клип N из M», и на живой игре она меняться не должна;
 *  • снизу — сколько роликов у уровня описано в ФАЙЛЕ сценария. Такой
 *    ролик из админки не убрать: сид вернёт его на ближайшей выкатке,
 *    и кнопка, чей результат откатывается деплоем, хуже отсутствующей.
 *    Убирать его нужно в `content/scene-template.json`.
 *
 * Порядок роликов панель не переставляет. Файловые идут так, как описаны
 * в сценарии, доснятые — в порядке добавления; перестановка отсюда
 * прожила бы до ближайшей выкатки у первых и переклеила бы цепочку
 * переходов у вторых.
 *
 * УДАЛЕНИЕ ДОСНЯТОГО РОЛИКА УНОСИТ УЖЕ СНЯТОЕ — `media_assets` всех
 * ассистенток разом, каскадом, без возврата. Поэтому сервер такой набор
 * сперва отбивает 409, панель показывает, сколько роликов сгорит, и только
 * повторное нажатие сохраняет. Согласия спрашивают ровно тогда, когда есть
 * что терять: пустой ролик уходит молча — лишний вопрос про «ничего»
 * приучает жать «да» не глядя.
 *
 * Итога «сколько заказать и почём» здесь нет, и ноль вместо него был бы
 * хуже молчания: сколько роликов купится, зависит от того, сколько игроков
 * дойдёт до уровня, — этого не знает никто, и уж точно не панель. Ролик
 * заказывается сам, когда игрок заходит на уровень.
 */

import { useState } from 'react'
import { MAX_CLIPS_PER_LEVEL } from '@lust/shared'
import { ApiError, request } from '../../api/client.js'
import {
  PRESET_TARGET_TITLES,
  type LevelClipNode,
  type PresetTarget,
  type SceneSetResponse,
  type TemplateRow,
} from '../../api/types.js'
import { Badge, Card, Notice } from '../../components/ui.js'
import { catalogOptions, toggleTemplate } from './state.js'
import { TemplateCatalog } from './TemplateCatalog.js'

export interface SetPanelProps {
  sceneId: string
  sceneTitle: string
  /** Кому назначаем: «Все характеры» или один характер. */
  target: PresetTarget
  /** Ролики уровня — из них берётся и стартовый выбор, и нижняя граница. */
  clips: LevelClipNode[]
  onClose: () => void
  onSaved: () => void
}

export function SetPanel({
  sceneId,
  sceneTitle,
  target,
  clips,
  onClose,
  onSaved,
}: SetPanelProps): JSX.Element {
  /**
   * Стартовый выбор — то, чем уровень снимается для этой цели СЕЙЧАС,
   * включая унаследованное от «Все характеры». Показывать характеру пустоту
   * там, где ролики на самом деле снимаются, значило бы врать: владелец
   * решил бы, что не настроено ничего.
   *
   * Ролик без шаблона места в списке не занимает: выбор — это список
   * шаблонов, а «пустое место посередине» нечем ни показать, ни отправить.
   * Такой ролик достанется следующему выбранному шаблону по своему месту.
   */
  const [selected, setSelected] = useState<string[]>(() =>
    clips.map((clip) => clip.promptPresetId).filter((id): id is string => id !== null && id !== ''),
  )
  const [templates, setTemplates] = useState<TemplateRow[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * Отказ 409: набор убирает ролики, по которым уже что-то снято. Держим
   * текст сервера, а не свою прикидку, — число сгорающих роликов знает
   * только он.
   */
  const [confirmDrop, setConfirmDrop] = useState<string | null>(null)

  const nameOf = new Map(catalogOptions(templates, selected).map((t) => [t.id, t.name]))

  /** Характер, которому уже назначено своё, можно вернуть к «Все характеры». */
  const inherited = clips.length > 0 && clips.every((clip) => clip.inherited)
  const canReset = target !== 'all' && !inherited

  /**
   * Состав правит только «Все характеры»: ролики у всех характеров одни
   * и те же, своим у характера бывает лишь шаблон. Поэтому вкладке
   * характера остаётся прежнее «ровно по одному на ролик».
   */
  const editsComposition = target === 'all'
  const fileClips = clips.filter((clip) => !clip.addedByAdmin).length
  const min = editsComposition ? Math.max(fileClips, 1) : clips.length
  const max = editsComposition ? MAX_CLIPS_PER_LEVEL : clips.length
  const sizeMismatch = selected.length < min || selected.length > max

  /** Сколько роликов добавится и сколько уйдёт, если сохранить сейчас. */
  const added = Math.max(0, selected.length - clips.length)
  const removed = Math.max(0, clips.length - selected.length)

  const save = async (confirm: boolean): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await request<SceneSetResponse>(`/admin/generation/scenes/${encodeURIComponent(sceneId)}/clips`, {
        method: 'PUT',
        body: { target, prompt_preset_ids: selected, confirm_drop: confirm },
      })
      onSaved()
      onClose()
    } catch (err) {
      if (err instanceof ApiError && err.code === 'clips_in_use') setConfirmDrop(err.message)
      else setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Снять своё и вернуться к «Все характеры». Подтверждения нет намеренно:
   * удаляется только сама настройка — ролики, уже снятые прежним шаблоном,
   * остаются у игроков.
   */
  const reset = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await request(`/admin/generation/scenes/${encodeURIComponent(sceneId)}/clips`, {
        method: 'DELETE',
        body: { target },
      })
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title={`Чем снимать: ${sceneTitle}`}
      hint={
        editsComposition
          ? `Шаблон по умолчанию для всех характеров. Сколько шаблонов выберете — столько роликов уровень и покажет, от ${String(min)} до ${String(max)}, по одному шаблону на ролик. Уже снятые ролики останутся как есть: новый шаблон достанется следующим заказам.`
          : `Только характер «${PRESET_TARGET_TITLES[target]}». Выберите по шаблону на каждый ролик уровня (их ${String(clips.length)}); остальные характеры это не затронет. Сколько у уровня роликов, меняется на вкладке «Все характеры».`
      }
      actions={
        <div className="row">
          <button className="btn btn--ghost btn--sm" type="button" disabled={busy} onClick={onClose}>
            Закрыть
          </button>
        </div>
      }
    >
      <div
        className="row row--top"
        style={{ gap: 'var(--gap-lg)', alignItems: 'flex-start', flexWrap: 'nowrap' }}
      >
        <div style={{ flex: '1 1 60%', minWidth: 0 }}>
          <TemplateCatalog
            selected={selected}
            onToggle={(id) => setSelected((prev) => toggleTemplate(prev, id))}
            onLoaded={setTemplates}
            maxHeight={420}
          />
        </div>

        <div className="stack stack--sm" style={{ flex: '0 0 340px', minWidth: 300 }}>
          <strong>Выбрано ({selected.length})</strong>

          {selected.length === 0 && (
            /**
             * Пугать словом «пусто» здесь неверно: терять нечего, и первый
             * выбранный шаблон достанется уже лежащему ролику — переснимать
             * ничего не придётся.
             */
            <Notice tone="warn">
              {clips.length === 0
                ? `У уровня пока нет роликов. Выберите от 1 до ${String(max)} шаблонов — столько роликов уровень и начнёт показывать.`
                : `У уровня есть ролики (${String(clips.length)}), но шаблон партнёра им не назначен. Пока его нет, заказ не ставится вовсе, и игрок видит «она готовится…» бесконечно.`}
            </Notice>
          )}

          {selected.map((id, index) => (
            <div key={id} className="row" style={{ justifyContent: 'space-between' }}>
              <div style={{ minWidth: 0 }}>
                <div>
                  {index + 1} · {nameOf.get(id) ?? id}
                </div>
                {/* Файловый ролик убрать отсюда нельзя — об этом честнее
                    сказать бейджем рядом, чем отказом после клика. */}
                {index < fileClips && (
                  <Badge tone="muted">ролик из файла сценария</Badge>
                )}
              </div>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => setSelected((prev) => toggleTemplate(prev, id))}
              >
                ✕
              </button>
            </div>
          ))}

          <div
            className="stack stack--sm"
            style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--gap-sm)' }}
          >
            {sizeMismatch && editsComposition && (
              <Notice tone="warn">
                {selected.length > max
                  ? `Больше ${String(max)} роликов у уровня быть не может — столько обещает индикатор «клип N из M» в плеере.`
                  : `Выберите хотя бы ${String(min)}: ${
                      fileClips > 0
                        ? `${String(fileClips)} роликов уровня описаны в файле сценария, и убрать их отсюда нельзя — сид вернёт их на ближайшей выкатке.`
                        : 'уровень без роликов игроку не показывается вовсе.'
                    }`}
              </Notice>
            )}

            {sizeMismatch && !editsComposition && (
              <Notice tone="warn">
                Выберите ровно {clips.length} {clips.length === 1 ? 'шаблон' : 'шаблона'} — по одному
                на каждый ролик уровня. Сколько у уровня роликов, меняется на вкладке «Все
                характеры».
              </Notice>
            )}

            {!sizeMismatch && (
              <span className="muted" style={{ fontSize: 12 }}>
                {editsComposition
                  ? 'Шаблоны достанутся ассистенткам всех характеров, кроме тех, кому назначено своё.'
                  : `Шаблоны достанутся только ассистенткам характера «${PRESET_TARGET_TITLES[target]}».`}{' '}
                Ролик закажется сам, когда игрок зайдёт на уровень; уже снятые останутся как есть.
              </span>
            )}

            {!sizeMismatch && editsComposition && added > 0 && (
              <Notice tone="muted">
                У уровня станет {selected.length} роликов вместо {clips.length}: новые снимутся
                у ассистенток, которые дойдут до уровня после сохранения.
              </Notice>
            )}

            {!sizeMismatch && editsComposition && removed > 0 && confirmDrop === null && (
              <Notice tone="warn">
                У уровня останется {selected.length} роликов вместо {clips.length}. Лишние удалятся
                вместе со всем, что по ним уже снято, — это не отменить.
              </Notice>
            )}

            {confirmDrop !== null && <Notice tone="error">{confirmDrop}</Notice>}

            {error !== null && <Notice tone="error">{error}</Notice>}

            <div className="row">
              {confirmDrop === null ? (
                <button
                  className="btn btn--sm"
                  type="button"
                  disabled={busy || selected.length === 0 || sizeMismatch}
                  onClick={() => void save(false)}
                >
                  {busy ? 'Сохраняю…' : 'Сохранить'}
                </button>
              ) : (
                /* Второе нажатие — уже осознанное: текст рядом называет
                   число роликов, которые сгорят. */
                <>
                  <button
                    className="btn btn--sm btn--danger"
                    type="button"
                    disabled={busy}
                    onClick={() => void save(true)}
                  >
                    {busy ? 'Удаляю…' : 'Удалить снятое и сохранить'}
                  </button>
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    disabled={busy}
                    onClick={() => setConfirmDrop(null)}
                  >
                    Отмена
                  </button>
                </>
              )}
              {canReset && confirmDrop === null && (
                <button className="btn btn--ghost btn--sm" type="button" disabled={busy} onClick={() => void reset()}>
                  вернуть к «Все характеры»
                </button>
              )}
            </div>

            {target !== 'all' && inherited && <Badge tone="muted">сейчас как у всех</Badge>}
          </div>
        </div>
      </div>
    </Card>
  )
}
